import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

const BG = "#05070a";
const MUTED = "#94a3b8";
const BORDER = "rgba(99,102,241,0.18)";

export default function OpsVerify() {
  const [status, setStatus] = useState<"loading" | "error">("loading");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setStatus("error");
      return;
    }

    fetch(`/api/auth/ops-verify?token=${encodeURIComponent(token)}`, {
      credentials: "same-origin",
    })
      .then(async (res) => {
        // Server returns JSON — never follow redirects so we can
        // distinguish a valid admin session from a failed/expired token.
        const data = await res.json().catch(() => ({}));
        if (data.success && data.redirectTo) {
          window.location.href = data.redirectTo;
          return;
        }
        setStatus("error");
      })
      .catch(() => {
        setStatus("error");
      });
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: BG }}
    >
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center mb-10">
          <img src={vexisLogo} alt="VEXIS" className="h-10 w-auto" />
        </div>

        <div
          className="rounded-2xl p-8"
          style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }}
        >
          {status === "loading" ? (
            <div className="space-y-4">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-indigo-400" />
              <p className="text-sm font-medium text-white">Verifying access…</p>
              <p className="text-sm" style={{ color: MUTED }}>Please wait.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto"
                style={{ backgroundColor: "rgba(239,68,68,0.12)" }}
              >
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <p className="text-xl font-semibold text-white">
                This link has expired or is invalid
              </p>
              <p className="text-sm" style={{ color: MUTED }}>
                Links are valid for 15 minutes and can only be used once.
              </p>
              <Link href="/ops">
                <Button
                  className="w-full font-semibold text-white"
                  style={{ backgroundColor: "#6366f1" }}
                  data-testid="button-ops-retry"
                >
                  Request a new link
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
